import {
  Children,
  createContext,
  Fragment,
  isValidElement,
  useContext,
  type ReactElement,
} from 'react';
import { Link } from 'react-router';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { useIsTruncated } from '@/shared/hooks/useIsTruncated/useIsTruncated';
import { PageHeaderInfoButton } from './PageHeaderInfoButton';
import type {
  PageHeaderBreadcrumbLinkProps,
  PageHeaderBreadcrumbPageProps,
  PageHeaderBreadcrumbProps,
  PageHeaderBreadcrumbSize,
} from './PageHeader.types';

/**
 * The size reaches the crumbs through context rather than a prop on each one:
 * a caller sets the trail's size once, and a crumb it does not own — an
 * editable name, an info button — still comes out at the same size.
 *
 * Every class here goes through the Tessra part's own `className`, which
 * tailwind-merges it against the part's `text-sm` base. Setting it on a
 * wrapper instead would leave both sizes on the element with source order
 * deciding which one wins.
 */
const BreadcrumbSizeContext = createContext<PageHeaderBreadcrumbSize>('default');

const SIZE_CLASS: Record<PageHeaderBreadcrumbSize, string> = {
  default: '',
  xs: 'type-label-xxs',
};

function useBreadcrumbSizeClass(): string {
  return SIZE_CLASS[useContext(BreadcrumbSizeContext)];
}

/** A truncated crumb is the only place its text is written, so the tooltip carries it whole. */
function FullTextWhenTruncated(props: {
  text: string;
  isTruncated: boolean;
  children: ReactElement;
}) {
  if (!props.isTruncated) return props.children;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{props.children}</TooltipTrigger>
      <TooltipContent side="bottom">{props.text}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Breadcrumb trail for records nested inside a section.
 *
 * Compositional rather than driven by an items array: in the design the
 * editable page name IS the trailing crumb, which no flat data shape can
 * express without a union type or a render prop. Callers pass crumbs as
 * children and this owns only the separators and the layout.
 *
 * Trails of unbounded depth (a nested folder tree) pass `maxItems`: the
 * leading crumbs collapse into an ellipsis so the current page and its nearest
 * ancestors keep their width instead of truncating to a few characters each.
 */
export function PageHeaderBreadcrumb(props: PageHeaderBreadcrumbProps) {
  const { maxItems, size = 'default' } = props;
  const allCrumbs = Children.toArray(props.children);
  const isCollapsed = maxItems != null && allCrumbs.length > maxItems;
  const crumbs = isCollapsed ? allCrumbs.slice(allCrumbs.length - maxItems) : allCrumbs;

  return (
    <BreadcrumbSizeContext.Provider value={size}>
      <TooltipProvider>
        <div className="flex min-w-0 items-center gap-3xs">
          <Breadcrumb data-testid="page-header-breadcrumb" className="min-w-0">
            {/* The `sm:` modifier is matched deliberately: BreadcrumbList ships
              `gap-1 sm:gap-1`, and tailwind-merge drops only the unprefixed half,
              so a plain `gap-1.5` loses to the surviving variant above 640px.
              The list carries the size too: the separators are its own text. */}
            <BreadcrumbList className={`flex-nowrap gap-1.5 sm:gap-1.5 ${SIZE_CLASS[size]}`}>
              {isCollapsed && (
                <BreadcrumbItem>
                  <BreadcrumbEllipsis data-testid="page-header-breadcrumb-ellipsis" />
                </BreadcrumbItem>
              )}
              {crumbs.map((crumb, index) => (
                // Keyed off the child, not the index: the trailing crumb can be
                // PageHeaderEditableName, which holds an in-progress draft that a
                // positional key would re-parent onto a different crumb if a
                // conditional one appeared while the field was open. Children
                // .toArray always assigns a key, honouring the caller's if set.
                <Fragment key={isValidElement(crumb) ? crumb.key : index}>
                  {(index > 0 || isCollapsed) && (
                    <BreadcrumbSeparator className="text-muted-foreground">/</BreadcrumbSeparator>
                  )}
                  <BreadcrumbItem className="min-w-0">{crumb}</BreadcrumbItem>
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
          {props.info && (
            <PageHeaderInfoButton page={props.info.page} onClick={props.info.onClick} />
          )}
        </div>
      </TooltipProvider>
    </BreadcrumbSizeContext.Provider>
  );
}

/**
 * A crumb that navigates. Every crumb before the current page is one of these.
 *
 * `no-underline` overrides Tessra's `BreadcrumbLink`, which underlines on hover
 * where the design changes only the colour. The important suffix is load-bearing:
 * both classes reach the element and Tailwind's source order otherwise wins.
 */
export function PageHeaderBreadcrumbLink(props: PageHeaderBreadcrumbLinkProps) {
  const sizeClass = useBreadcrumbSizeClass();
  const { setNode, isTruncated } = useIsTruncated();

  return (
    <FullTextWhenTruncated text={props.children} isTruncated={isTruncated}>
      <BreadcrumbLink asChild className={sizeClass}>
        <Link ref={setNode} to={props.href} className="truncate hover:no-underline!">
          {props.children}
        </Link>
      </BreadcrumbLink>
    </FullTextWhenTruncated>
  );
}

/** The trailing crumb when the current page's name is not editable. */
export function PageHeaderBreadcrumbPage(props: PageHeaderBreadcrumbPageProps) {
  const sizeClass = useBreadcrumbSizeClass();
  const { setNode, isTruncated } = useIsTruncated();

  return (
    <FullTextWhenTruncated text={props.children} isTruncated={isTruncated}>
      <BreadcrumbPage ref={setNode} className={`truncate ${sizeClass}`}>
        {props.children}
      </BreadcrumbPage>
    </FullTextWhenTruncated>
  );
}
